import { useMemo } from 'react';
import type { AgendyAisOvmDto } from '@gov-nx/api';
import { ovmQuery, useAxiosInstance, usePoQuery } from '@gov-nx/api';
import { normalizeStr } from '@gov-nx/utils/common';

interface OvmSearchAutocompleteProps {
	maAgendy: boolean;
}

interface AgendyAisOvmDtoWithNormalizedData extends AgendyAisOvmDto {
	_normalizedNazev: string;
}

const HARD_DATA_LENGTH_LIMIT = 1000;

const filteredRecords = (
	normalizedData: AgendyAisOvmDtoWithNormalizedData[],
	input: string
) => {
	if (input.trim() === '') {
		return normalizedData.slice(0, HARD_DATA_LENGTH_LIMIT);
	}

	const normalizedInput = normalizeStr(input);

	const matches: AgendyAisOvmDtoWithNormalizedData[] = [];
	for (let i = 0; i < normalizedData.length; i++) {
		const node = normalizedData[i];
		if (node._normalizedNazev.includes(normalizedInput)) {
			matches.push(node);
		}
		if (matches.length >= HARD_DATA_LENGTH_LIMIT) {
			break;
		}
	}

	return matches;
};

export const useOvmSearchAutocomplete = ({
	maAgendy,
}: OvmSearchAutocompleteProps) => {
	const { axiosInstance } = useAxiosInstance();

	const { data } = usePoQuery({
		queryKey: ['ovm', maAgendy],
		queryFn: () => ovmQuery(axiosInstance, maAgendy),
	});

	const normalizedData = useMemo(
		() =>
			(data?.seznam ?? []).map<AgendyAisOvmDtoWithNormalizedData>((item) => ({
				...item,
				_normalizedNazev: normalizeStr(item.nazev ?? ''),
			})),
		[data]
	);

	return {
		searchCallback: async (input: string) =>
			filteredRecords(normalizedData, input),
	};
};
